import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ──────────────────────────────────────────────────────────────────────

type TestType = 'image' | 'title' | 'price' | 'bullets';

const MP_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES',
};
type TestStatus = 'draft' | 'running_control' | 'running_variant' | 'completed' | 'cancelled';

interface SplitTest {
  id: number;
  account_id: string;
  asin: string;
  sku: string;
  test_name: string;
  test_type: TestType;
  status: TestStatus;
  control_value: string;
  variant_value: string;
  ppc_keywords: string;
  daily_budget: number;
  bid: number;
  days_per_variant: number;
  auto_apply_winner: number;
  control_start: string | null;
  control_end: string | null;
  variant_start: string | null;
  variant_end: string | null;
  control_impressions: number | null;
  control_clicks: number | null;
  control_orders: number | null;
  control_spend: number | null;
  control_sales: number | null;
  variant_impressions: number | null;
  variant_clicks: number | null;
  variant_orders: number | null;
  variant_spend: number | null;
  variant_sales: number | null;
  winner: 'control' | 'variant' | null;
  confidence: number | null;
  created_at: string;
  updated_at: string;
}

interface SplitTestsResponse {
  tests: SplitTest[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<TestStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  running_control: 'bg-blue-100 text-blue-700',
  running_variant: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<TestStatus, string> = {
  draft: 'Draft',
  running_control: 'Running (Control)',
  running_variant: 'Running (Variant)',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const TYPE_ICONS: Record<TestType, string> = {
  image: '\uD83D\uDDBC\uFE0F',
  title: '\uD83D\uDCDD',
  price: '\uD83D\uDCB2',
  bullets: '\uD83D\uDD39',
};

function pct(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '--';
  return (n * 100).toFixed(2) + '%';
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '--';
  return n.toLocaleString();
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '--';
  return '$' + n.toFixed(2);
}

function ctr(clicks: number | null, impressions: number | null): number | null {
  if (!clicks || !impressions) return null;
  return clicks / impressions;
}

function cvr(orders: number | null, clicks: number | null): number | null {
  if (!orders || !clicks) return null;
  return orders / clicks;
}

function roas(sales: number | null, spend: number | null): string {
  if (!sales || !spend) return '--';
  return (sales / spend).toFixed(2) + 'x';
}

function dayProgress(start: string | null, _end: string | null, totalDays: number): { current: number; total: number } {
  if (!start) return { current: 0, total: totalDays };
  const s = new Date(start).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - s) / (1000 * 60 * 60 * 24)) + 1;
  return { current: Math.min(elapsed, totalDays), total: totalDays };
}

// ── Component ──────────────────────────────────────────────────────────────────

export function SplitTests() {
  const { displayName } = useNicknames();

  const [tests, setTests] = useState<SplitTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedTest, setSelectedTest] = useState<SplitTest | null>(null);
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Product catalog for picker
  const [catalogProducts, setCatalogProducts] = useState<Array<{ asin: string; sku: string; title: string; account_id: string; marketplace: string; image_url: string | null; price: number | null }>>([]);
  const [formProduct, setFormProduct] = useState(''); // "asin|sku|account_id|marketplace"

  // Form state (derived from product selection)
  const selectedProduct = catalogProducts.find(p => `${p.asin}|${p.sku}|${p.account_id}|${p.marketplace}` === formProduct);
  const formAccountId = selectedProduct?.account_id ?? '';
  const formAsin = selectedProduct?.asin ?? '';
  const formSku = selectedProduct?.sku ?? '';
  const formMarketplace = selectedProduct?.marketplace ?? '';
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<TestType>('image');
  const [formControl, setFormControl] = useState('');
  const [formVariant, setFormVariant] = useState('');
  const [formKeywords, setFormKeywords] = useState('');
  const [formBudget, setFormBudget] = useState(15);
  const [formBid, setFormBid] = useState(0.75);
  const [formDays, setFormDays] = useState(7);
  const [formAutoApply, setFormAutoApply] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadTests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<SplitTestsResponse>('/split-tests');
      setTests(res.tests ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await api.post<{ rows: Array<{ asin: string; sku: string; title: string; account_id: string; marketplace: string; image_url: string | null; price: number | null }> }>(
        '/admin/query',
        { sql: `SELECT c.asin, c.sku, COALESCE(p.nickname, c.title) as title, c.account_id, c.marketplace, COALESCE(p.image_url, c.image_url) as image_url, c.price FROM amazon_catalog c LEFT JOIN amazon_products p ON p.sku = c.sku WHERE c.asin IS NOT NULL AND LOWER(c.status) = 'active' AND c.sku NOT LIKE 'amzn.gr.%' ORDER BY c.marketplace, title` },
      );
      setCatalogProducts(res.rows ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { loadTests(); }, [loadTests]);

  // ── Summary stats ──────────────────────────────────────────────────────────

  const totalTests = tests.length;
  const running = tests.filter(t => t.status === 'running_control' || t.status === 'running_variant').length;
  const completed = tests.filter(t => t.status === 'completed').length;
  const wins = tests.filter(t => t.status === 'completed' && t.winner === 'variant').length;
  const winRate = completed > 0 ? ((wins / completed) * 100).toFixed(0) + '%' : '--';

  // ── Sorting ────────────────────────────────────────────────────────────────

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  const sorted = [...tests].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const ak = (a as unknown as Record<string, unknown>)[sortKey];
    const bk = (b as unknown as Record<string, unknown>)[sortKey];
    if (ak == null && bk == null) return 0;
    if (ak == null) return dir;
    if (bk == null) return -dir;
    if (typeof ak === 'string') return ak.localeCompare(bk as string) * dir;
    return ((ak as number) - (bk as number)) * dir;
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  async function handleAction(id: number, action: 'launch' | 'cancel' | 'apply' | 'delete') {
    setActionLoading(id);
    try {
      if (action === 'delete') {
        await api.delete(`/split-tests/${id}`);
      } else {
        await api.post(`/split-tests/${id}/${action}`);
      }
      await loadTests();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);
    try {
      if (!formProduct) { setFormError('Select a product'); setFormSubmitting(false); return; }
      const keywords = formKeywords.split('\n').map(k => k.trim()).filter(Boolean);
      if (keywords.length < 3) { setFormError('At least 3 keywords required'); setFormSubmitting(false); return; }
      await api.post('/split-tests', {
        accountId: formAccountId,
        marketplace: formMarketplace,
        asin: formAsin,
        sku: formSku,
        testName: formName,
        testType: formType,
        controlValue: formControl,
        variantValue: formVariant,
        ppcKeywords: keywords,
        ppcDailyBudget: formBudget,
        ppcBid: formBid,
        daysPerVariant: formDays,
        autoApply: formAutoApply,
      });
      setShowForm(false);
      setFormProduct(''); setFormName(''); setFormControl(''); setFormVariant('');
      setFormKeywords(''); setFormBudget(15); setFormBid(0.75); setFormDays(7); setFormAutoApply(false);
      await loadTests();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to create test');
    } finally {
      setFormSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const SortHeader = ({ label, field }: { label: string; field: string }) => (
    <th
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
      onClick={() => toggleSort(field)}
    >
      {label} {sortKey === field ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Split Testing</h1>
          <p className="text-sm text-gray-500 mt-1">PPC-based A/B testing for listing elements -- first-party accurate</p>
        </div>
        <button
          onClick={() => setShowForm(f => !f)}
          className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-medium"
        >
          {showForm ? 'Cancel' : '+ New Test'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Tests', value: totalTests, color: 'text-gray-900' },
          { label: 'Running', value: running, color: 'text-blue-600' },
          { label: 'Completed', value: completed, color: 'text-green-600' },
          { label: 'Win Rate', value: winRate, color: 'text-purple-600' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Test</h2>
          {formError && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{formError}</div>}
          <form onSubmit={handleCreate} className="space-y-4">
            {/* Product Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={formProduct} required
                onChange={e => {
                  setFormProduct(e.target.value);
                  // Auto-populate control value for title/price tests
                  const prod = catalogProducts.find(p => `${p.asin}|${p.sku}|${p.account_id}|${p.marketplace}` === e.target.value);
                  if (prod && formType === 'price' && prod.price) setFormControl(String(prod.price));
                  if (prod && formType === 'title') setFormControl(prod.title);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">Select a product...</option>
                {Array.from(new Set(catalogProducts.map(p => p.marketplace))).map(mp => (
                  <optgroup key={mp} label={MP_LABELS[mp] ?? mp}>
                    {catalogProducts.filter(p => p.marketplace === mp).map(p => (
                      <option key={`${p.asin}|${p.sku}|${p.account_id}|${p.marketplace}`} value={`${p.asin}|${p.sku}|${p.account_id}|${p.marketplace}`}>
                        {p.title} ({p.sku})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {selectedProduct && (
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                  {selectedProduct.image_url && <img src={selectedProduct.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                  <span className="font-medium text-gray-600">{MP_LABELS[selectedProduct.marketplace] ?? selectedProduct.marketplace}</span>
                  <span>· ASIN: {selectedProduct.asin} · SKU: {selectedProduct.sku}</span>
                  {selectedProduct.price && <span>· ${selectedProduct.price}</span>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Test Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
                <input
                  type="text" required value={formName} onChange={e => setFormName(e.target.value)}
                  placeholder="e.g., Main image lifestyle vs white bg"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              {/* Test Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Type</label>
                <select
                  value={formType} onChange={e => setFormType(e.target.value as TestType)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="image">Image</option>
                  <option value="title">Title</option>
                  <option value="price">Price</option>
                  <option value="bullets">Bullets</option>
                </select>
              </div>
            </div>

            {/* Control / Variant values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Control Value</label>
                {(formType === 'bullets' || formType === 'title') ? (
                  <textarea
                    required rows={3} value={formControl} onChange={e => setFormControl(e.target.value)}
                    placeholder="Current listing value"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                ) : (
                  <input
                    type="text" required value={formControl} onChange={e => setFormControl(e.target.value)}
                    placeholder={formType === 'image' ? 'Image URL' : formType === 'price' ? '29.99' : 'Current value'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Variant Value</label>
                {(formType === 'bullets' || formType === 'title') ? (
                  <textarea
                    required rows={3} value={formVariant} onChange={e => setFormVariant(e.target.value)}
                    placeholder="New listing value to test"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                ) : (
                  <input
                    type="text" required value={formVariant} onChange={e => setFormVariant(e.target.value)}
                    placeholder={formType === 'image' ? 'Image URL' : formType === 'price' ? '24.99' : 'New value to test'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                )}
              </div>
            </div>

            {/* PPC Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PPC Keywords (one per line, min 3)</label>
              <textarea
                required rows={4} value={formKeywords} onChange={e => setFormKeywords(e.target.value)}
                placeholder={"keyword one\nkeyword two\nkeyword three"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Budget / Bid / Days */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Budget ($)</label>
                <input
                  type="number" step="0.01" min="1" value={formBudget}
                  onChange={e => setFormBudget(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bid ($)</label>
                <input
                  type="number" step="0.01" min="0.02" value={formBid}
                  onChange={e => setFormBid(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days per Variant</label>
                <input
                  type="number" min="3" max="30" value={formDays}
                  onChange={e => setFormDays(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox" checked={formAutoApply}
                    onChange={e => setFormAutoApply(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Auto-apply winner
                </label>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit" disabled={formSubmitting}
                className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-medium disabled:opacity-50"
              >
                {formSubmitting ? 'Creating...' : 'Create Test'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error */}
      {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm">{error}</div>}

      {/* Tests Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader label="Test Name" field="test_name" />
                <SortHeader label="ASIN / SKU" field="asin" />
                <SortHeader label="Type" field="test_type" />
                <SortHeader label="Status" field="status" />
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phase</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Control CTR / CVR</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant CTR / CVR</th>
                <SortHeader label="Winner" field="winner" />
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lift</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400">No tests yet. Create one to get started.</td></tr>
              ) : sorted.map(t => {
                const controlCtr = ctr(t.control_clicks, t.control_impressions);
                const controlCvr = cvr(t.control_orders, t.control_clicks);
                const variantCtr = ctr(t.variant_clicks, t.variant_impressions);
                const variantCvr = cvr(t.variant_orders, t.variant_clicks);
                const lift = (controlCvr && variantCvr) ? ((variantCvr - controlCvr) / controlCvr) : null;

                // Determine phase progress
                let phase: { current: number; total: number };
                if (t.status === 'running_control') {
                  phase = dayProgress(t.control_start, t.control_end, t.days_per_variant);
                } else if (t.status === 'running_variant') {
                  phase = dayProgress(t.variant_start, t.variant_end, t.days_per_variant);
                } else {
                  phase = { current: t.status === 'completed' ? t.days_per_variant : 0, total: t.days_per_variant };
                }

                return (
                  <tr
                    key={t.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedTest(selectedTest?.id === t.id ? null : t)}
                  >
                    <td className="px-3 py-3 text-sm font-medium text-gray-900">{t.test_name}</td>
                    <td className="px-3 py-3 text-sm">
                      <div className="font-medium text-gray-900">{t.asin}</div>
                      <div className="text-xs text-gray-500">{displayName(t.sku)}</div>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                        {TYPE_ICONS[t.test_type]} {t.test_type}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                        {STATUS_LABELS[t.status]}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${t.status === 'running_variant' ? 'bg-purple-500' : 'bg-blue-500'}`}
                            style={{ width: `${phase.total ? (phase.current / phase.total) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">Day {phase.current}/{phase.total}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600">
                      {pct(controlCtr)} / {pct(controlCvr)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600">
                      {pct(variantCtr)} / {pct(variantCvr)}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {t.winner ? (
                        <span className="inline-flex items-center gap-1 text-yellow-600 font-medium">
                          <span className="text-base leading-none">{'\uD83C\uDFC6'}</span>
                          {t.winner === 'control' ? 'Control' : 'Variant'}
                          {t.confidence != null && <span className="text-xs text-gray-400 ml-1">({(t.confidence * 100).toFixed(0)}%)</span>}
                        </span>
                      ) : <span className="text-gray-400">--</span>}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {lift != null ? (
                        <span className={lift >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          {lift >= 0 ? '+' : ''}{(lift * 100).toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-400">--</span>}
                    </td>
                    <td className="px-3 py-3 text-sm" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {t.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleAction(t.id, 'launch')}
                              disabled={actionLoading === t.id}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >Launch</button>
                            <button
                              onClick={() => { if (confirm('Delete this test?')) handleAction(t.id, 'delete'); }}
                              disabled={actionLoading === t.id}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                            >Delete</button>
                          </>
                        )}
                        {(t.status === 'running_control' || t.status === 'running_variant') && (
                          <button
                            onClick={() => handleAction(t.id, 'cancel')}
                            disabled={actionLoading === t.id}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                          >Cancel</button>
                        )}
                        {t.status === 'completed' && t.winner && (
                          <button
                            onClick={() => handleAction(t.id, 'apply')}
                            disabled={actionLoading === t.id}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >Apply Winner</button>
                        )}
                        {(t.status === 'completed' || t.status === 'cancelled') && (
                          <button
                            onClick={() => { if (confirm('Delete this test?')) handleAction(t.id, 'delete'); }}
                            disabled={actionLoading === t.id}
                            className="px-2 py-1 text-xs text-gray-400 hover:text-red-600 disabled:opacity-50"
                            title="Delete"
                          >✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Test Detail Modal */}
      {selectedTest && (
        <TestDetailModal test={selectedTest} onClose={() => setSelectedTest(null)} />
      )}
    </div>
  );
}

// ── Test Detail Modal ────────────────────────────────────────────────────────

function TestDetailModal({ test, onClose }: { test: SplitTest; onClose: () => void }) {
  const t = test;
  const controlCtr = ctr(t.control_clicks, t.control_impressions);
  const controlCvr = cvr(t.control_orders, t.control_clicks);
  const variantCtr = ctr(t.variant_clicks, t.variant_impressions);
  const variantCvr = cvr(t.variant_orders, t.variant_clicks);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t.test_name}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t.asin} &middot; {t.sku} &middot;{' '}
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                  {STATUS_LABELS[t.status]}
                </span>
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>

          {/* Side-by-Side Comparison */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Comparison</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Control</p>
                {t.test_type === 'image' ? (
                  <img src={t.control_value} alt="Control" className="w-full h-48 object-contain rounded bg-gray-50" />
                ) : (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{t.control_value}</p>
                )}
              </div>
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/30">
                <p className="text-xs font-medium text-purple-600 mb-2 uppercase">Variant</p>
                {t.test_type === 'image' ? (
                  <img src={t.variant_value} alt="Variant" className="w-full h-48 object-contain rounded bg-gray-50" />
                ) : (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{t.variant_value}</p>
                )}
              </div>
            </div>
          </div>

          {/* Metrics Comparison */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Metrics</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">Metric</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Control</th>
                  <th className="text-right py-2 text-purple-600 font-medium">Variant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { label: 'Impressions', c: fmtNum(t.control_impressions), v: fmtNum(t.variant_impressions) },
                  { label: 'Clicks', c: fmtNum(t.control_clicks), v: fmtNum(t.variant_clicks) },
                  { label: 'CTR', c: pct(controlCtr), v: pct(variantCtr) },
                  { label: 'Orders', c: fmtNum(t.control_orders), v: fmtNum(t.variant_orders) },
                  { label: 'CVR', c: pct(controlCvr), v: pct(variantCvr) },
                  { label: 'Spend', c: fmtMoney(t.control_spend), v: fmtMoney(t.variant_spend) },
                  { label: 'Sales', c: fmtMoney(t.control_sales), v: fmtMoney(t.variant_sales) },
                  { label: 'ROAS', c: roas(t.control_sales, t.control_spend), v: roas(t.variant_sales, t.variant_spend) },
                ].map(row => (
                  <tr key={row.label}>
                    <td className="py-2 text-gray-700">{row.label}</td>
                    <td className="py-2 text-right text-gray-900">{row.c}</td>
                    <td className="py-2 text-right text-gray-900">{row.v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Confidence Bar */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Statistical Confidence</h3>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className={`h-4 rounded-full transition-all ${
                    (t.confidence ?? 0) >= 0.95 ? 'bg-green-500' :
                    (t.confidence ?? 0) >= 0.80 ? 'bg-yellow-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${(t.confidence ?? 0) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700 w-12 text-right">
                {t.confidence != null ? `${(t.confidence * 100).toFixed(0)}%` : '--'}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">95%+ recommended for a confident result</p>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Timeline</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <TimelineStep label="Created" date={t.created_at} active />
              <TimelineArrow />
              <TimelineStep
                label="Control Phase"
                date={t.control_start ? `${fmtDate(t.control_start)} - ${t.control_end ? fmtDate(t.control_end) : 'ongoing'}` : null}
                active={t.status === 'running_control' || t.status === 'running_variant' || t.status === 'completed'}
              />
              <TimelineArrow />
              <TimelineStep
                label="Variant Phase"
                date={t.variant_start ? `${fmtDate(t.variant_start)} - ${t.variant_end ? fmtDate(t.variant_end) : 'ongoing'}` : null}
                active={t.status === 'running_variant' || t.status === 'completed'}
              />
              <TimelineArrow />
              <TimelineStep
                label="Result"
                date={t.status === 'completed' ? (t.winner ? `Winner: ${t.winner}` : 'No winner') : null}
                active={t.status === 'completed'}
              />
            </div>
          </div>

          {/* PPC Keywords */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">PPC Keywords</h3>
            <div className="flex flex-wrap gap-1">
              {(t.ppc_keywords ?? '').split('\n').filter(Boolean).map((kw, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{kw}</span>
              ))}
            </div>
          </div>

          {/* Config Details */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Daily Budget</p>
              <p className="font-medium">${t.daily_budget}</p>
            </div>
            <div>
              <p className="text-gray-500">Bid</p>
              <p className="font-medium">${t.bid}</p>
            </div>
            <div>
              <p className="text-gray-500">Days per Variant</p>
              <p className="font-medium">{t.days_per_variant}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Timeline sub-components ──────────────────────────────────────────────────

function TimelineStep({ label, date, active }: { label: string; date: string | null; active: boolean }) {
  return (
    <div className={`flex flex-col items-center px-3 py-2 rounded-lg ${active ? 'bg-brand-50 border border-brand-200' : 'bg-gray-50 border border-gray-200'}`}>
      <span className={`text-xs font-medium ${active ? 'text-brand-700' : 'text-gray-400'}`}>{label}</span>
      {date && <span className="text-[10px] text-gray-400 mt-0.5">{date}</span>}
    </div>
  );
}

function TimelineArrow() {
  return <span className="text-gray-300 text-sm">{'\u2192'}</span>;
}

function fmtDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return d;
  }
}
